import { AppointmentForm } from "~/classes/AppointmentForm";
import { GetterTree, ActionTree, MutationTree } from "vuex";
import { AppointmentSchedule } from "@ambii-com/core";

export const state = () => ({
  menuId: null as string,
  isSaveProfile: false as boolean,
  appointmentPatientCount: null as number,
  appointmentForms: [] as AppointmentForm[],
  appointmentTimeSlots: [] as Map<string, string>[],
  selectedRelatedPatients: [] as Map<string, RelatedPatient>[],
});

type AppointmentFormState = ReturnType<typeof state>;
interface RelatedPatient {
  id: string; lastName: string;
  firstName: string;
  // add other relevant fields
}

export const getters: GetterTree<AppointmentFormState, any> = {};

export const mutations: MutationTree<AppointmentFormState> = {
  SET_MENUID(state, menuId: MenuId) {
    state.menuId = menuId
    localStorage.setItem('menuId', menuId);
  },
  SET_IS_SAVE_PROFILE(state, isSaveProfile: boolean) {
    state.isSaveProfile = isSaveProfile
  },
  SET_APPOINTMENT_PATIENT_COUNT(state, appointmentPatientCount: number) {
    if (appointmentPatientCount < 1) {
      throw new Error('Appointment patient count must be positive');
    }
    state.appointmentPatientCount = appointmentPatientCount;
    localStorage.setItem('appointmentPatientCount', appointmentPatientCount.toString());
  },
  SET_APPOINTMENT_FORMS(state, appointmentForms: AppointmentForm[]) {
    state.appointmentForms = appointmentForms;
  },
  SET_APPOINTMENT_TIME_SLOTS(state, appointmentTimeSlots: Array<Map<string, string>>) {
    state.appointmentTimeSlots = appointmentTimeSlots;
    localStorage.setItem('appointmentTimeSlots', JSON.stringify(appointmentTimeSlots));
  },
  SET_SELECTED_RELATED_PATIENTS(state, selectedRelatedPatients: Map<string, RelatedPatient>[]) {
    state.selectedRelatedPatients = selectedRelatedPatients;
    localStorage.setItem('selectedRelatedPatients', JSON.stringify(selectedRelatedPatients));
  },
  RESET_GUEST_FIELDS(state) {
    if(state.appointmentForms.length > 0) {
      state.appointmentForms[0] = state.appointmentForms[0].resetGuestFields();
    }
  },
  RESET_APPOINTMENT_FORMS(state) {
    state.appointmentForms = [];
  },
  RESET_MENUID(state) {
    state.menuId = null
  },
  RESET_APPOINTMENT_TIME_SLOTS(state) {
    state.appointmentTimeSlots = [];
  },
  REMOVE_LAST_TIME_SLOT(state) {
    state.appointmentTimeSlots.pop();
  }
};

export const actions: ActionTree<AppointmentFormState, any> = {
  loadFromLocalStorage({ commit }) {
    const appointmentForms = localStorage.getItem('appointmentForms');
    const appointmentTimeSlots = localStorage.getItem('appointmentTimeSlots');
    const appointmentPatientCount = localStorage.getItem('appointmentPatientCount');
    const menuId = localStorage.getItem('menuId');
    if (appointmentForms) {
      const formsArray = JSON.parse(appointmentForms).map(plainToAppointmentForm);
      commit('SET_APPOINTMENT_FORMS', formsArray);
    }
    if (appointmentTimeSlots) {
      commit('SET_APPOINTMENT_TIME_SLOTS', JSON.parse(appointmentTimeSlots));
    }
    if (appointmentPatientCount) {
      commit('SET_APPOINTMENT_PATIENT_COUNT', parseInt(appointmentPatientCount));
    }
    if (menuId) {
      commit('SET_MENUID', menuId);
    }
  },
  setMenuId({ commit },  menuId: MenuId) {
    commit("SET_MENUID", menuId);
  },
  setIsSaveProfile({ commit },  isSaveProfile: boolean) {
    commit("SET_IS_SAVE_PROFILE", isSaveProfile);
  },
  setAppointmentPatientCount({ commit }, appointmentPatientCount: number) {
    commit("SET_APPOINTMENT_PATIENT_COUNT", appointmentPatientCount);
  },
  setAppointmentForms({ commit }, appointmentForms: AppointmentForm[]) {
    try {
      if (!Array.isArray(appointmentForms)) {
        throw new Error('appointmentForms must be an array');
      }
      if (appointmentForms.some(form => !(form instanceof AppointmentForm))) {
        throw new Error('All elements must be instances of AppointmentForm');
      }
      commit("SET_APPOINTMENT_FORMS", appointmentForms);
      const plainForms = appointmentForms.map(appointmentFormToPlain);
      localStorage.setItem('appointmentForms', JSON.stringify(plainForms));
    } catch (error) {
      console.error('Failed to set appointment forms:', error);
      throw error;
    }
  },
  setAppointmentTimeSlots({ commit }, appointmentTimeSlots: Array<Map<string, string>>) {
    commit("SET_APPOINTMENT_TIME_SLOTS", appointmentTimeSlots);
  },
  setSelectedRelatedPatients({ commit }, selectedRelatedPatients: Map<string, RelatedPatient>[]) {
    commit("SET_SELECTED_RELATED_PATIENTS", selectedRelatedPatients);
  },
  resetMenuId({ commit }) {
    commit("RESET_MENUID");
  },
  resetGuestFields({ commit }) {
    commit("RESET_GUEST_FIELDS");
  },
  resetAppointmentForms({ commit }) {
    commit("RESET_APPOINTMENT_FORMS");
  },
  resetAppointmentTimeSlots({ commit }) {
    commit("RESET_APPOINTMENT_TIME_SLOTS");
  },
  removeLastTimeSlot({ commit }) {
    commit("REMOVE_LAST_TIME_SLOT");
  }
};

const appointmentFormToPlain = (form: AppointmentForm) => {
  return {
    ...form,
    schedule: { ...form.schedule }
  };
}

const plainToAppointmentForm = (plain: any): AppointmentForm => {
  const schedule = new AppointmentSchedule(
    plain.schedule.id,
    plain.schedule.clinicId,
    plain.schedule.resourceId,
    plain.schedule.date,
    plain.schedule.startDatetime,
    plain.schedule.endDatetime,
    plain.schedule.maxNumbers,
    plain.schedule.intervalMinutes,
    plain.schedule.appliedNumbers,
    plain.schedule.receptionStartDatetime,
    plain.schedule.receptionEndDatetime,
    plain.schedule.status,
    plain.schedule.clinicRegion,
  );

  return new AppointmentForm(
    schedule,
    plain.firstName,
    plain.lastName,
    plain.firstNameKana,
    plain.lastNameKana,
    plain.tel,
    plain.dob,
    plain.cardNumber,
    plain.sex,
    plain.freeInput
  );
}